import { FC, useContext, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  BePaidIcon,
  BonusIcon,
  Button,
  ClockIcon,
  CloudPaymentsIcon,
  DateRangePicker,
  H2,
  InfoBlock,
  KaspiIcon,
  Label,
  PrinterIcon,
  SBPIcon,
  Select,
  Separator,
  ShopIcon,
  StripeIcon,
  Table,
  Tabs,
  WalletIcon,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { SummaryBlock } from 'components';
import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { formatDateForServer } from 'utils';

import s from './Sales.module.scss';
import { EXPORT_SALES_REPORT, GENERATE_SALES_REPORT } from './schema';
import {
  ECategory,
  IExportSalesReportResponse,
  ISalesReportRequest,
  ISalesReportResponse,
  TNormalizedItem,
} from './types';
import { formatMoney, getCategoryLabel } from './utils';

export const Sales: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);

  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();

  const [error, setError] = useState<string | null>(null);

  // const [, setIsOpenCsv] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ECategory | null>(
    null
  );

  const COLUMNS: Array<Column<TNormalizedItem>> = useMemo(
    () => [
      {
        accessor: 'title',
        Header: t('report.sales.position'),
        Cell: ({ value }) => {
          if (value === 'DEPOSIT') return t('payments.category.deposit');

          if (value === 'BONUS') return t('category.bonus');

          if (value === 'CLOUD_PAYMENTS') return 'CloudPayments';

          if (value === 'TINKOFF_SBP') return t('sbp');

          if (value === 'KASPI_PAY') return 'Kaspi-онлайн';

          if (value === 'STRIPE') return 'Stripe';
          if (value === 'BEPAID_ERIP') return 'bePaid';

          return value;
        },
      },
      {
        accessor: 'category',
        Header: t('category'),
        Cell: ({ value }) => getCategoryLabel(value, t),
        disableResizing: true,
        width: 199,
      },
      {
        accessor: 'sold',
        Header: t('report.sales.sold'),
        Cell: ({ value }) => `${value} ${t('pcs')}`,
        disableResizing: true,
        width: 128,
      },
      {
        accessor: 'sum',
        Header: t('sum'),
        Cell: ({ value }) => formatMoney(value, currency),
        disableResizing: true,
        width: 147,
      },
      {
        accessor: 'averagePrice',
        Header: t('report.sales.average.price'),
        Cell: ({ value }) => formatMoney(value, currency),
        disableResizing: true,
        width: 157,
      },
      {
        accessor: ({ refundCount, title }) => {
          if (['CLOUD_PAYMENTS', 'TINKOFF_SBP', 'KASPI_PAY'].includes(title))
            return null;

          return refundCount;
        },
        Header: t('report.sales.refunds.count'),
        Cell: ({ value }: { value: number }) => (
          <Label
            text={value.toString()}
            iconName="CircleCloseIcon"
            color="gray-primary"
            rounded
          />
        ),
        disableResizing: true,
        width: 167,
      },
    ],
    [currency, t]
  );

  const [generateReport, { data: response, loading, called, variables }] =
    useLazyQuery<ISalesReportResponse, ISalesReportRequest>(
      GENERATE_SALES_REPORT,
      {
        fetchPolicy: 'no-cache',
        onError: (error) => {
          setError(error.message);
        },
      }
    );
  const [generateReportFile, { loading: reportFileLoading }] = useLazyQuery<
    IExportSalesReportResponse,
    ISalesReportRequest
  >(EXPORT_SALES_REPORT, {
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (!response) return;

      open(response.salesReport.url, '_self');
      // setIsOpenCsv(false);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // const [generateReportFileSwitch, { loading: reportSwitchFileLoading }] =
  //   useLazyQuery<IExportSalesSwitchReportResponse, ISalesReportRequest>(
  //     EXPORT_SALES,
  //     {
  //       fetchPolicy: 'no-cache',
  //       onCompleted: (response) => {
  //         if (!response) return;
  //
  //         open(response.exportSales, '_self');
  //         setIsOpenCsv(false);
  //       },
  //       onError: () => {
  //         setError({
  //           title: 'errors.somethingGoesWrong',
  //           message: 'errors.common',
  //         });
  //       },
  //     }
  //   );

  const normalizedData = useMemo(
    () =>
      response?.salesReport.data.map<TNormalizedItem>(({ values }, index) => {
        const title = response.salesReport.keys[index];
        const category = response.salesReport.extraLabels[index] as ECategory;
        const [sold, sum, averagePrice, refundCount] = values;

        return {
          title,
          category,
          sold,
          sum,
          averagePrice,
          refundCount,
        };
      }) ?? [],
    [response]
  );

  const summary = useMemo(() => {
    const totalsData = {
      depositSum: 0,
      depositCount: 0,
      bonusSum: 0,
      bonusCount: 0,
      tariffsSum: 0,
      tariffsCount: 0,
      goodsSum: 0,
      goodsCount: 0,
      servicesSum: 0,
      servicesCount: 0,
      cloudPaymentsSum: 0,
      cloudPaymentsCount: 0,
      tinkoffSbpSum: 0,
      tinkoffSbpCount: 0,
      kaspiPaySum: 0,
      kaspiPayCount: 0,
      StripeSum: 0,
      StripeCount: 0,
      BePaidEripSum: 0,
      BePaidEripCount: 0,
    };

    response?.salesReport.summary.forEach((item) => {
      if (item.title === 'cloudpayments sum')
        totalsData.cloudPaymentsSum = item.value;
      if (item.title === 'cloudpayments count')
        totalsData.cloudPaymentsCount = item.value;
      if (item.title === 'deposit sum') totalsData.depositSum = item.value;
      if (item.title === 'deposit count') totalsData.depositCount = item.value;
      if (item.title === 'bonus sum') totalsData.bonusSum = item.value;
      if (item.title === 'bonus count') totalsData.bonusCount = item.value;
      if (item.title === 'tariff sum') totalsData.tariffsSum = item.value;
      if (item.title === 'tariff count') totalsData.tariffsCount = item.value;
      if (item.title === 'good sum') totalsData.goodsSum = item.value;
      if (item.title === 'good count') totalsData.goodsCount = item.value;
      if (item.title === 'service sum') totalsData.servicesSum = item.value;
      if (item.title === 'service count') totalsData.servicesCount = item.value;
      if (item.title === 'tinkoff sbp sum')
        totalsData.tinkoffSbpSum = item.value;
      if (item.title === 'tinkoff sbp count')
        totalsData.tinkoffSbpCount = item.value;
      if (item.title === 'kaspi pay sum') totalsData.kaspiPaySum = item.value;
      if (item.title === 'kaspi pay count')
        totalsData.kaspiPayCount = item.value;
      if (item.title === 'stripe sum') totalsData.StripeSum = item.value;
      if (item.title === 'stripe count') totalsData.StripeCount = item.value;
      if (item.title === 'bepaid erip count')
        totalsData.BePaidEripCount = item.value;
      if (item.title === 'bepaid erip sum')
        totalsData.BePaidEripSum = item.value;
    });

    return totalsData;
  }, [response]);

  const filteredData = useMemo(() => {
    if (!selectedCategory) return normalizedData;

    return normalizedData.filter(
      ({ category }) => category === selectedCategory
    );
  }, [normalizedData, selectedCategory]);

  // Добавить когда будет экспорт с выбором
  // const dropdownRef = useRef<HTMLDivElement>(null);
  //
  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       dropdownRef.current &&
  //       !dropdownRef.current.contains(event.target as Node)
  //     ) {
  //       setIsOpenCsv(false);
  //     }
  //   };
  //
  //   document.addEventListener('click', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('click', handleClickOutside);
  //   };
  // }, [setIsOpenCsv]);

  const size = useWindowResize();

  return (
    <div className={s.container} data-onboarding="report-sales-result">
      {error && (
        <InfoBlock
          className={s.info_error}
          title={t('errors.error')}
          message={error}
          direction="row"
        />
      )}
      <div
        className={s.generate}
        data-onboarding="report-sales-generate"
        data-onboarding-report-generated={String(
          Boolean(response?.salesReport)
        )}
      >
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={dayjs().format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />
        <div className={s.buttons_block}>
          <Button
            onClick={() => {
              setError(null);

              const input: ISalesReportRequest['input'] = {
                from: formatDateForServer(dayjs(startDate)),
                to: formatDateForServer(dayjs(endDate)),
              };

              return generateReport({ variables: { input } });
            }}
            disabled={loading}
          >
            {t('reports.generate')}
          </Button>
          {/*  <div className={s.export_csv}>*/}
          {/*    <Button*/}
          {/*      iconName="IncomeIcon"*/}
          {/*      onClick={(event) => {*/}
          {/*        event.stopPropagation();*/}
          {/*        setIsOpenCsv((prev) => !prev);*/}
          {/*      }}*/}
          {/*      disabled={!variables?.input}*/}
          {/*      isLoading={reportFileLoading || reportSwitchFileLoading}*/}
          {/*      className={cn(*/}
          {/*        s.export,*/}
          {/*        (reportFileLoading || reportSwitchFileLoading) && s.loading*/}
          {/*      )}*/}
          {/*      variant="secondary"*/}
          {/*    >*/}
          {/*      {t('export.csv')}*/}
          {/*    </Button>*/}
          {/*    {!(reportFileLoading || reportSwitchFileLoading) && (*/}
          {/*      <div className={s.arrow}>*/}
          {/*        {isOpenCsv ? (*/}
          {/*          <TopIcon*/}
          {/*            size="l"*/}
          {/*            color={!variables?.input ? 'disable' : 'link-info'}*/}
          {/*          />*/}
          {/*        ) : (*/}
          {/*          <BottomIcon*/}
          {/*            size="l"*/}
          {/*            color={!variables?.input ? 'disable' : 'link-info'}*/}
          {/*          />*/}
          {/*        )}*/}
          {/*      </div>*/}
          {/*    )}*/}

          {/*    {isOpenCsv && (*/}
          {/*      <div*/}
          {/*        ref={dropdownRef}*/}
          {/*        className={cn(*/}
          {/*          s.dropdown,*/}
          {/*          (reportFileLoading || reportSwitchFileLoading) && s.disabled*/}
          {/*        )}*/}
          {/*      >*/}
          {/*        <div*/}
          {/*          className={cn(*/}
          {/*            (reportFileLoading || reportSwitchFileLoading) && s.disabled,*/}
          {/*            s.drop_item*/}
          {/*          )}*/}
          {/*          onClick={() => generateReportFile({ variables })}*/}
          {/*        >*/}
          {/*          {t('sales.export.consolidated')}*/}
          {/*        </div>*/}
          {/*        <div*/}
          {/*          className={cn(*/}
          {/*            (reportFileLoading || reportSwitchFileLoading) && s.disabled,*/}
          {/*            s.drop_item*/}
          {/*          )}*/}
          {/*          onClick={() => generateReportFileSwitch({ variables })}*/}
          {/*        >*/}
          {/*          {t('sales.export.all')}*/}
          {/*        </div>*/}
          {/*      </div>*/}
          {/*    )}*/}
          {/*  </div>*/}
          {/*</div>*/}
          {/*<div className={s.data}>*/}
          <Button
            iconName="IncomeIcon"
            onClick={() => generateReportFile({ variables })}
            disabled={!variables?.input}
            isLoading={reportFileLoading}
            className={s.export_csv}
            variant="outline"
          >
            {size.width < 1864 && size.width > 960 ? t('csv') : t('export.csv')}
          </Button>
        </div>
      </div>
      {response?.salesReport?.summary && (
        <div className={s.controls}>
          <H2>{t('report.sales.title')}</H2>
        </div>
      )}
      {response?.salesReport?.summary && (
        <div className={s.summary}>
          {/*надо будет доделать в дургой задаче*/}
          {/*<div className={s.left_block}>*/}
          {/*  <div className={s.top}>*/}
          {/*    <div className={s.iconBlock}>*/}
          {/*      <CoinsIcon />*/}
          {/*    </div>*/}
          {/*    <span className={s.total}>*/}
          {/*      {t('total')}:{' '}*/}
          {/*      {summary.depositSum*/}
          {/*        ? formatMoney(summary.depositSum, currency)*/}
          {/*        : '—'}*/}
          {/*    </span>*/}
          {/*  </div>*/}
          {/*  <Separator style={{ marginBottom: '18px' }} />*/}
          {/*  <div className={s.bottom}>*/}
          {/*    <div className={s.bottom_item}>*/}
          {/*      <span>*/}
          {/*        {summary.depositSum*/}
          {/*          ? formatMoney(summary.depositSum, currency)*/}
          {/*          : '—'}*/}
          {/*      </span>*/}
          {/*      <span className={s.bottom_title}>{t('money.cash')}</span>*/}
          {/*    </div>*/}
          {/*    <div className={s.bottom_item}>*/}
          {/*      <span>*/}
          {/*        {summary.depositSum*/}
          {/*          ? formatMoney(summary.depositSum, currency)*/}
          {/*          : '—'}*/}
          {/*      </span>*/}
          {/*      <span className={s.bottom_title}>{t('money.card')}</span>*/}
          {/*    </div>*/}
          {/*    <div className={s.bottom_item}>*/}
          {/*      <span>*/}
          {/*        {summary.depositSum*/}
          {/*          ? formatMoney(summary.depositSum, currency)*/}
          {/*          : '—'}*/}
          {/*      </span>*/}
          {/*      <span className={s.bottom_title}>{t('online.pay')}</span>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*</div>*/}
          {!!summary.depositSum && (
            <SummaryBlock
              Icon={WalletIcon}
              color="violet"
              topText={
                summary.depositSum
                  ? formatMoney(summary.depositSum, currency)
                  : '—'
              }
              bottomText={
                summary.depositCount
                  ? `${t('report.total.deposits')}: ${summary.depositCount}`
                  : t('report.total.deposits')
              }
            />
          )}
          {!!summary.tinkoffSbpSum && (
            <SummaryBlock
              Icon={SBPIcon}
              color="pink"
              topText={
                summary.tinkoffSbpSum
                  ? formatMoney(summary.tinkoffSbpSum, currency)
                  : '—'
              }
              bottomText={
                summary.tinkoffSbpCount
                  ? `${t('report.total.sbp')}: ${summary.tinkoffSbpCount}`
                  : t('report.total.sbp')
              }
            />
          )}
          {!!summary.kaspiPaySum && (
            <SummaryBlock
              Icon={KaspiIcon}
              color="red"
              topText={
                summary.kaspiPaySum
                  ? formatMoney(summary.servicesSum, currency)
                  : '—'
              }
              bottomText={
                summary.kaspiPayCount
                  ? `${t('report.total.kaspi')}: ${summary.servicesCount}`
                  : t('report.total.kaspi')
              }
            />
          )}
          {!!summary.cloudPaymentsSum && (
            <SummaryBlock
              Icon={CloudPaymentsIcon}
              color="blue"
              topText={
                summary.cloudPaymentsSum
                  ? formatMoney(summary.cloudPaymentsSum, currency)
                  : '—'
              }
              bottomText={
                summary.cloudPaymentsCount
                  ? `${t('report.total.cloud_payments')}: ${
                      summary.cloudPaymentsCount
                    }`
                  : t('report.total.cloud_payments')
              }
            />
          )}
          {!!summary.tariffsSum && (
            <SummaryBlock
              Icon={ClockIcon}
              color="blue"
              topText={
                summary.tariffsSum
                  ? formatMoney(summary.tariffsSum, currency)
                  : '—'
              }
              bottomText={
                summary.tariffsCount
                  ? `${t('report.total.tariffs')}: ${summary.tariffsCount}`
                  : t('report.total.tariffs')
              }
            />
          )}
          {!!summary.goodsSum && (
            <SummaryBlock
              Icon={ShopIcon}
              color="green"
              topText={
                summary.goodsSum ? formatMoney(summary.goodsSum, currency) : '—'
              }
              bottomText={
                summary.goodsCount
                  ? `${t('report.total.goods')}: ${summary.goodsCount}`
                  : t('report.total.goods')
              }
            />
          )}
          {!!summary.servicesSum && (
            <SummaryBlock
              Icon={PrinterIcon}
              color="gray"
              topText={
                summary.servicesSum
                  ? formatMoney(summary.servicesSum, currency)
                  : '—'
              }
              bottomText={
                summary.servicesCount
                  ? `${t('report.total.services')}: ${summary.servicesCount}`
                  : t('report.total.services')
              }
            />
          )}
          {!!summary.bonusSum && (
            <SummaryBlock
              Icon={BonusIcon}
              color="orange"
              topText={
                summary.bonusSum ? formatMoney(summary.bonusSum, currency) : '—'
              }
              bottomText={
                summary.bonusCount
                  ? `${t('report.total.bonuses')}: ${summary.bonusCount}`
                  : t('report.total.bonuses')
              }
            />
          )}
          {!!summary.kaspiPaySum && (
            <SummaryBlock
              Icon={KaspiIcon}
              color="red"
              topText={
                summary.kaspiPaySum
                  ? formatMoney(summary.kaspiPaySum, currency)
                  : '—'
              }
              bottomText={
                summary.kaspiPayCount
                  ? `Kaspi: ${summary.kaspiPayCount}`
                  : 'Kaspi'
              }
            />
          )}
          {!!summary.StripeSum && (
            <SummaryBlock
              Icon={StripeIcon}
              color="violet"
              topText={
                summary.StripeSum
                  ? formatMoney(summary.StripeSum, currency)
                  : '—'
              }
              bottomText={
                summary.StripeCount
                  ? `Stripe: ${summary.StripeCount}`
                  : 'Stripe'
              }
            />
          )}
          {!!summary.BePaidEripSum && (
            <SummaryBlock
              Icon={BePaidIcon}
              color="gray"
              topText={
                summary.BePaidEripSum
                  ? formatMoney(summary.BePaidEripSum, currency)
                  : '—'
              }
              bottomText={
                summary.BePaidEripCount
                  ? `bePaid: ${summary.BePaidEripCount}`
                  : 'bePaid'
              }
            />
          )}
        </div>
      )}
      {response?.salesReport?.summary && size.width < 960 && (
        <Separator style={{ marginBottom: '16px' }} />
      )}
      <div className={s.table_block}>
        {size.width > 960 ? (
          <Tabs
            options={[
              {
                value: null,
                title: t('all.sales'),
              },
              {
                value: ECategory.TARIFF,
                title: t('category.tariffs'),
              },
              {
                value: ECategory.GOOD,
                title: t('category.goods'),
              },
              {
                value: ECategory.SERVICE,
                title: t('category.services'),
              },
            ]}
            value={selectedCategory}
            onChange={setSelectedCategory}
            className={s.tabs}
          />
        ) : (
          <Select
            name="category"
            placeholder={t('all.sales')}
            options={[
              {
                value: ECategory.TARIFF,
                title: t('report.sales.category.tariffs'),
              },
              {
                value: ECategory.GOOD,
                title: t('report.sales.category.goods'),
              },
              {
                value: ECategory.SERVICE,
                title: t('report.sales.category.services'),
              },
            ]}
            value={selectedCategory}
            onChange={setSelectedCategory}
            errorWrapperClassName={s.category}
          />
        )}
        <Table
          data={filteredData}
          columns={COLUMNS}
          isLoading={loading}
          emptyDataPlaceholder={t(
            called ? 'report.placeholder' : 'report.placeholder.emptyDates'
          )}
          className={s.table}
        />
      </div>
    </div>
  );
};
